export const jobApplication = {
  name: "Job Application |||| Job Applications",
  statusUpdated: "Status updated successfully",
  statusUpdateError: "Error updating status",
  statusTransitionNotAllowed: "Cannot transition from \"%{currentStatus}\" to \"%{targetStatus}\". Allowed statuses: %{allowedStatuses}",
  postHireToPreHireTransition: "Cannot transition from post-hire status \"%{currentStatus}\" to pre-hire status \"%{targetStatus}\". Once an application enters a post-hire status, it cannot return to the pipeline.",
  outcomeToPipelineTransition: "Cannot transition from final outcome status \"%{currentStatus}\" to pipeline status \"%{targetStatus}\". Once an application reaches a final outcome, it cannot return to the pipeline.",
  skipHireStatus: "Cannot transition to post-hire status \"%{targetStatus}\" without first being hired. Applications must go through the \"Hired\" status first.",
  statusTransitionValidationWarning: "This status transition is not recommended. Please review the workflow configuration.",
  fields: {
    jobOpeningId: "Job Opening",
    candidateName: "Candidate Name",
    email: "Email",
    phone: "Phone",
    birthDate: "Birth Date",
    city: "City",
    state: "State",
    resumeUrl: "Resume URL",
    status: "Status",
    statusId: "Status",
    interviewDate: "Interview Date",
    notes: "Notes",
    observations: "Observations",
    sourceId: "Source",
    source: "Source",
    behavioralProfile: "Behavioral Profile",
    companyId: "Company",
    interviewResponsibleId: "Interview Responsible",
    interviewSchedulingResponsibleId: "Interview Scheduling Responsible",
    supervisorId: "Supervisor",
    startedAt: "Start Date",
    outcome: "Outcome",
    // Column header on the list ("Closure - date - reason"); `outcome` stays
    // as-is because the edit form reuses it as a field label.
    closureOutcome: "Closure",
    outcomeAt: "Closure Date",
    closureReasonName: "Closure Reason",
    closureReasonId: "Closure Reason",
    closureReasonCategory: "Type",
    outcomeHelperText: "Derived automatically from the pipeline status. To hire or close, change the application status (with a reason where required).",
    outcomeChoices: {
      hired: "Hired",
      closed: "Closed",
      rejected: "Rejected",
      withdrawn: "Withdrawn",
      no_show: "No Show",
      offer_declined: "Offer Declined",
      terminated: "Terminated",
    },
    contactedAt: "First Contact Date",
    interviewScheduledAt: "Interview Scheduled Date",
    completedOnboardingAt: "Onboarding Completed Date",
    hiredAt: "Hired Date",
    terminatedAt: "Terminated Date",
    createdAt: "Created at",
    updatedAt: "Updated at",
  },
  filters: {
    createdFrom: "Created From",
    createdTo: "Created To",
    interviewFrom: "Interview From",
    interviewTo: "Interview To",
    operators: {
      contains: "Contains",
      equals: "Equals",
      notEquals: "Not equals",
      gte: "Greater than or equal to",
      lte: "Less than or equal to",
      in: "In",
      isEmpty: "Is empty",
      isNotEmpty: "Is not empty",
    },
  },
  actions: {
    dashboard: "Dashboard",
    calendar: "Interview Calendar",
  },
  behavioralProfile: {
    dominant: "Dominant",
    influential: "Influential",
    steady: "Steady",
    compliant: "Compliant",
  },
  tabs: {
    candidateData: "Candidate Data",
    profileAndReferences: "Profile and References",
    processAndObservations: "Process and Observations",
  },
  views: {
    kanban: "Kanban",
    list: "List",
    archive: "Archive",
    pipelineTooltip: "Column view (Kanban) showing only pipeline applications (without outcome or with recent outcome). Filters are applied on the frontend.",
    listTooltip: "Complete listing with all advanced filters. Search and analyze all applications.",
  },
  kanban: {
    hasMore: "Showing %{count} of %{total} applications. Use filters to refine the search or view the complete list in the \"List\" tab.",
  },
  audit: {
    button: "Audit",
    title: "Audit History",
    close: "Close",
    details: "Audit Details",
    dateTime: "Date and Time",
    user: "User",
    action: "Action",
    changes: "Changes Made",
    additionalInfo: "Additional Information",
    noChanges: "No field changes recorded for this action.",
    before: "Before",
    after: "After",
    metadata: {
      ip: "IP Address",
      userAgent: "Browser",
    },
    relatedChanges: {
      label: "Related Change",
      resourceLabel: "Related Resource",
      relatedResource: "Related Resource",
      statusRenamed: "Status \"%{oldName}\" was renamed to \"%{newName}\"",
      sourceRenamed: "Source \"%{oldName}\" was renamed to \"%{newName}\"",
      companyRenamed: "Company \"%{oldName}\" was renamed to \"%{newName}\"",
      userRenamed: "User \"%{oldName}\" was renamed to \"%{newName}\"",
      generic: "Related resource was changed",
    },
  },
  closeApplication: {
    title: "Close application",
    body: "Select a closure reason",
    reasonLabel: "Reason",
    notesLabel: "Notes (optional)",
    cancel: "Cancel",
    confirm: "Confirm",
    reasonRequired: "Select a closure reason before saving",
  },
  linkCandidateUser: {
    title: "Link candidate user",
    tabCreate: "Create new",
    tabLink: "Link existing",
    nameLabel: "Name",
    emailLabel: "Email",
    passwordLabel: "Temporary password",
    userLabel: "User",
    rolesLabel: "Roles",
    buttonOnDetail: "Link candidate user",
    successCreate: "User created and linked",
    successLink: "User linked",
    errorOnlyHired: "Only hired applications can have a linked user",
  },
};

export const jobApplicationSource = {
  name: "Job Application Source |||| Job Application Sources",
  fields: {
    name: "Name",
    description: "Description",
    createdAt: "Created at",
    updatedAt: "Updated at",
  },
};

export const jobApplicationClosureReason = {
  name: "Closure Reason |||| Closure Reasons",
  fields: {
    name: "Name",
    category: "Category",
    phase: "Phase",
    isActive: "Active",
    order: "Order",
    createdAt: "Created at",
    updatedAt: "Updated at",
  },
  categoryChoices: {
    candidate_dropout: "Candidate dropout",
    candidate_decision: "Candidate decision",
    company_decision: "Company decision",
  },
  phaseChoices: {
    pre_hire: "Pre-hire",
    post_hire: "Post-hire",
  },
};

export const jobApplicationStatus = {
  name: "Job Application Status |||| Job Application Status",
  fields: {
    name: "Name",
    description: "Description",
    order: "Order",
    triggersMilestone: "Triggers Milestone",
    triggersOutcome: "Triggers Outcome",
    hidesFromKanban: "Hide from Kanban",
    autoHideAfterDays: "Auto-hide after (days)",
    isPostHire: "Post-hire",
    createdAt: "Created at",
    updatedAt: "Updated at",
  },
  milestoneLabels: {
    first_contact: "First Contact",
    interview_scheduled: "Interview Scheduled",
    started_work: "Started Work",
    completed_onboarding: "Completed Onboarding",
  },
  outcomeLabels: {
    hired: "Hired",
    rejected: "Rejected",
    withdrawn: "Withdrawn",
    no_show: "No Show",
    offer_declined: "Offer Declined",
    terminated: "Terminated",
  },
  filters: {
    operators: {
      contains: "Contains",
      equals: "Equals",
      notEquals: "Not equals",
      gte: "Greater than or equal to",
      lte: "Less than or equal to",
    },
  },
  workflow: {
    title: "Status Workflow Manager",
    subtitle: "Visualize and manage status transitions",
    loadingError: "Error loading workflow",
    deleteSuccess: "Transition deleted successfully",
    deleteError: "Error deleting transition",
    createSuccess: "Transition created successfully",
    createError: "Error creating transition",
    selfLoopWarning: "Cannot create transition to the same status",
    duplicateTransition: "This transition already exists",
    selectedTransition: "Selected transition ID: %{id}",
    deleteButton: "Delete Transition",
    refreshButton: "Refresh",
    viewColumns: "Column View",
    viewGraph: "Graph View",
    confirmDelete: "Are you sure you want to delete this transition?",
    instructions: "How to use",
    instructionsText: "To create a transition: drag from the source node (bottom handle) to the target node (top handle). To delete: click on an edge (line) and use the 'Delete Transition' button.",
    modal: {
      statusInfo: "Status Information",
      outgoingTransitions: "Outgoing Transitions",
      incomingTransitions: "Incoming Transitions",
      noOutgoingTransitions: "No outgoing transitions",
      noIncomingTransitions: "No incoming transitions",
      addTransition: "Add New Transition",
      selectTargetStatus: "Select target status",
      createTransition: "Create Transition",
      close: "Close",
    },
    nodeTypes: {
      pipeline: "Pipeline",
      postHire: "Post-Hire",
      outcome: "Outcome",
    },
    legend: {
      title: "Legend",
      pipeline: "Selection process statuses",
      postHire: "Statuses after hiring",
      outcome: "Final outcome statuses",
    },
    enforceValidationLabel: "Enforce transitions",
    validationDisabledWarning: "Transition validation is disabled. Any status transition is allowed at the moment.",
    configUpdateSuccess: "Configuration updated successfully",
    configUpdateError: "Error updating configuration",
    nodeLabels: {
      milestone: "Milestone: %{milestone}",
      outcome: "Outcome: %{outcome}",
    },
    nodeTypesV2: {
      hire: "Hire",
      terminal: "Termination",
      postHire: "Post-Hire",
      pipeline: "Pipeline",
    },
    pipelineHealth: {
      tabBuilder: "Builder",
      tabHealth: "Pipeline Health",
      title: "Pipeline Health",
      subtitle: "Structural diagnosis of the hiring funnel",
      loadingError: "Error loading pipeline health",
      hireStatus: "Hire status",
      terminationStatus: "Termination status",
      notConfigured: "Not configured",
      warningsTitle: "Warnings",
      noWarnings: "No structural issues detected.",
      statusesTitle: "Pipeline statuses",
      flags: {
        hire: "Hire",
        terminal: "Terminal",
        requiresClosureReason: "Requires closure reason",
        postHire: "Post-hire",
        hiddenFromKanban: "Hidden from Kanban",
      },
      columns: {
        order: "Order",
        name: "Name",
        flags: "Flags",
      },
    },
  },
};

export const jobApplicationCalendar = {
  name: "Interview Calendar |||| Interview Calendars",
  title: "Interview Calendar",
  noInterviews: "No interviews for this day",
  noCompany: "No company",
  today: "Today",
  exportPdf: "Export PDF (print)",
  totalInterviews: "Total interviews for the day: %{count}",
};

export const jobApplicationDashboard = {
  name: "Applications",
  title: "Job Applications Dashboard",
  subtitle: "Recruitment overview for the selected period",
  dataSince: "Data since %{date}. Earlier records are excluded from the metrics.",
  filters: {
    startDate: "Start date",
    endDate: "End date",
  },
  stats: {
    total: "Total Applications",
    inPipeline: "In Pipeline",
    finalized: "Finalized",
    thisMonth: "This Month",
    contacted: "Contacted",
    interviews: "Interviews Scheduled",
    hired: "Hired",
    startedWork: "Started Work",
    completedOnboarding: "Completed Onboarding",
    interviewRate: "Interview Rate",
    hireRate: "Hire Rate",
    contactRate: "Contact Rate",
    startWorkRate: "Start Work Rate",
    onboardingRate: "Onboarding Rate",
  },
  charts: {
    byStatus: "Applications by Status",
    bySource: "Applications by Source",
    byCompany: "Applications by Company",
    hiredByCompany: "Hires by Company",
    byCity: "Applications by City",
    timeline: "Applications Timeline",
    behavioralProfile: "Behavioral Profile",
    funnel: "Conversion Funnel",
    byOutcome: "Outcomes Distribution",
    milestones: "Milestones Reached",
    statusPipeline: "Status: Pipeline vs Post-Hire",
    averageTime: "Average Time to Milestones",
    pipeline: "Pipeline",
    postHire: "Post-Hire",
    daily: "Daily",
    cumulative: "Cumulative",
    others: "Others",
    noSource: "No source",
    noCompany: "No company",
    noCity: "No city",
    moreCities: "more cities",
    hiredByCompanyAndSource: "Hires by Company and Source",
    noData: "No data available",
    tooltips: {
      timeline: "Shows the temporal evolution of applications created over time. The blue line represents the daily number of new applications, while the orange line shows the cumulative total since the start of the period.",
      byStatus: "Distribution of applications by current status. Shows how many applications are at each stage of the recruitment process.",
      bySource: "Origin of applications, showing which channels (websites, referrals, social media, etc.) are generating more candidates.",
      byCompany: "Distribution of applications by company/store. Useful for identifying which units are receiving more candidates.",
      byCity: "Geographic distribution of applications by city. Helps understand the geographic coverage of recruitment.",
      behavioralProfile: "Distribution of candidates' behavioral profiles (Analytical, Driver, Amiable, Expressive).",
      hiredByCompany: "Number of hired candidates by company/store. Shows which units are hiring more.",
      funnel: "Conversion funnel showing how many applications pass through each stage: Total → Contacted → Interviewed → Hired → Started Work → Completed Onboarding. The conversion rate shows the percentage that advanced from the previous stage.",
      byOutcome: "Distribution of final outcomes of applications (Hired, Rejected, Withdrawn, No Show, etc.). Applications still in pipeline don't have a defined outcome.",
      milestones: "Number of applications that reached each important milestone: First Contact, Interview Scheduled, Started Work, and Completed Onboarding.",
      statusPipeline: "Comparison between pipeline statuses (selection process) and post-hire statuses (after hiring). Helps visualize the complete process flow.",
      averageTime: "Average time in days from application creation to reaching each important milestone. Useful for identifying bottlenecks and optimizing the recruitment process. Based only on applications that actually reached each stage.",
      hiredByCompanyAndSource: "Grouped bar chart showing how many candidates were hired by each store and from which sources. Helps identify which stores are hiring more and which recruitment channels are most effective for each unit.",
    },
  },
  outcomes: {
    inPipeline: "In Pipeline",
  },
  dashboards: {
    title: "Dashboards",
    tabSourcing: "Sourcing",
    tabConversion: "Conversion",
    tabRetention: "Retention",
    tabOperations: "Operations",
    tabOverview: "Overview",
    dateRangeLabel: "Date range",
    sourcing: {
      total: "Total applications",
      contacted: "Contacted",
      contactRate: "Contact rate",
      bySource: "Applications by source",
      byCity: "Applications by city (Top 10)",
      byCompany: "Applications by company",
      timeline: "Timeline",
    },
    conversion: {
      funnel: "Conversion funnel",
      closureReasons: "Closure reasons (by category)",
      exitedFromStatus: "Exits by status",
      statusTimings: "Status timings",
      timingAvg: "Avg (days)",
      timingMin: "Min",
      timingMax: "Max",
      timingTotal: "Total",
      timingCount: "Count",
      funnelByStage: "Funnel by stage",
      stageReached: "Reached",
      stageExited: "Exited",
      stageConversionToNext: "Conversion to next",
      tooltips: {
        funnel:
          "How many candidates pass through each stage. Drop-off between stages shows where you lose people.",
        closureReasons:
          "Breakdown of why applications were closed in the period.",
        exitedFromStatus:
          "Which status the candidate left from when closed — shows WHERE in the funnel loss happens.",
        statusTimings:
          "Average/min/max time candidates spend in each status. Surfaces bottlenecks.",
        funnelByStage:
          "How many distinct candidates reached each stage of the ladder (measured from status history) and how many exited at each one. Shows exactly WHERE in the ladder the gap happens.",
      },
    },
    retention: {
      activeNow: "Active now",
      turnoverRate: "Turnover",
      avgDaysAsHired: "Avg days as hired (days)",
      cohort: "Cohort (hire month)",
      totalHired: "Hired",
      checkpoint30: "30 days",
      checkpoint60: "60 days",
      checkpoint90: "90 days",
      checkpoint180: "180 days",
      postHireReasons: "Post-hire termination reasons",
      turnoverByCategory: "Turnover by type",
      turnoverCategoryLabels: {
        candidate_decision: "Voluntary (resigned)",
        company_decision: "Involuntary (let go)",
        candidate_dropout: "Abandonment (ghosted)",
      },
      activeHeadcount: "Active employees",
      tenureActives: "Active tenure (avg/median)",
      tenureActivesValue: "%{avg}d / %{median}d",
      onboardingGhost: "Onboarding ghosts",
      tooltips: {
        turnoverRate:
          "Percentage of hires who left (terminated or resigned) in the period. Lower is better.",
        turnoverRateNoBase:
          "Not measurable yet: there were no active employees at the start of the period, so there is no base to compare against. This is expected in the first months after the analytics cutover — the rate shows up as soon as there is headcount at the period start.",
        avgDaysAsHired:
          "Average days a person stayed hired before being terminated. Shows how early people leave.",
        cohort:
          "Cohort = people hired in the same month. We track how many are still active 30/60/90/180 days after hire.",
        activeNow:
          "Hired people still active today (no termination recorded). Independent of the selected period.",
        postHireReasons:
          "Why people left AFTER being hired — helps tell if it's the job, commission or another factor.",
        turnoverByCategory:
          "Turnover composition by type: how much is people who RESIGNED (voluntary), how much the company LET GO (involuntary) and how much simply ABANDONED (ghosted).",
        activeHeadcount:
          "Current employees: people who became Active (from the cutover date on) and have not been terminated. Independent of the selected period.",
        tenureActives:
          "How long, in days, CURRENT employees have been on board (now minus hire date). Shows average and median; measures who stays, not only who left.",
        onboardingGhost:
          "How many signed (Contratado) and vanished during onboarding — closed while leaving Contratado/Documentation/Integration/Onboarding before becoming Active.",
      },
    },
    operations: {
      aging: "Aging by status",
      staleApplications: "Stale applications",
      candidateName: "Candidate",
      statusName: "Status",
      daysInStatus: "Days in status",
      bucket: "Bucket",
      count: "Count",
      avgDaysToFirstContact: "Avg. time to first contact",
      daysValue: "%{days} days",
      uncontactedQueue: "Awaiting first contact",
      tooltips: {
        aging:
          "How long each application has been stuck in its current status, grouped in buckets (0-3d, 4-7d, 8-15d, 15d+). Red = attention.",
        staleApplications:
          "Applications stuck 7+ days with no status change — candidates for follow-up or closure.",
        avgDaysToFirstContact:
          "Average days from application creation to first contact, over candidates contacted on/after the cutover date. Answers \"is HR slow to reach out?\".",
        uncontactedQueue:
          "Applications still without first contact (in Sourcing/awaiting), grouped by how long ago they were created (0-1d, 2-3d, 4-7d, 7d+). Red buckets = attention.",
      },
    },
    overview: {
      newInPeriod: "New in period",
      hiredInPeriod: "Hired in period",
      activeNow: "Active now",
      turnoverInPeriod: "Turnover in period",
      stuckOver7Days: "Stuck +7d",
      totalInPipeline: "In pipeline",
      tooltips: {
        newInPeriod: "Applications created within the selected period.",
        hiredInPeriod:
          "People hired within the period (by hire date).",
        activeNow:
          "Hired people still active today. Current state — does not change with the period.",
        turnoverInPeriod:
          "Terminations recorded in the period. Current outflow of hires.",
        stuckOver7Days:
          "Applications with no status change for over 7 days. Current state, ignores period.",
        totalInPipeline:
          "Total applications still in progress (no final outcome). Current state.",
      },
    },
  },
};
